import { useBoolean } from '@hover/blueprint';
import { useCallback, useMemo, useState } from 'react';
import { matchPath, useHistory, useLocation } from 'react-router';
import { number, object } from 'yup';

import type {
  AiDisplayImageFragment,
  AiGeneratedImageFragment,
} from 'src/shared/graphql/generated';
import { useGetProjectsQuery } from 'src/shared/graphql/generated';
import { parseParams } from 'src/shared/hooks/useParsedParams';
import { Route, generatePath } from 'src/shared/route';
import { logError } from 'src/shared/telemetry/logger';
import { isDevelopment } from 'src/shared/utils/environment';

import { useRoutes } from '../../hooks';
import type { SaveToProjectState } from '../../schemas';
import { customizeReferrerSchema } from '../../schemas';

export interface UseCloseCustomizeProps {
  image: AiDisplayImageFragment | null | undefined;
  customizedImage?: AiGeneratedImageFragment | null;
}

export const useCloseCustomize = ({ image }: UseCloseCustomizeProps) => {
  const history = useHistory();
  const { state } = useLocation();

  const { preload: preloadRoute, load: loadRoute } = useRoutes();

  const { referrer } = customizeReferrerSchema.isValidSync(state)
    ? state
    : { referrer: null };

  const [isClosing, setIsClosing] = useState(false);
  const [hasPreloadedClose, { on: onPreloadClose }] = useBoolean(false);

  const closePathname = useMemo(() => {
    // Default case for when the customize route is hit directly
    // or route state is malformed for some reason
    if (!image) return Route.HOMEOWNER_APP_HOME.path;

    const { aiOriginalImageId, id: aiDisplayImageId } = image;

    if (
      !referrer ||
      matchPath(referrer, { path: Route.HOMEOWNER_APP_ORIGINAL.path })
    ) {
      return generatePath(
        Route.HOMEOWNER_APP_ORIGINAL_DISPLAY_FULLSCREEN.path,
        {
          aiOriginalImageId,
          aiDisplayImageId,
        },
      );
    }

    if (matchPath(referrer, { path: Route.HOMEOWNER_APP_SAVED.path })) {
      return generatePath(Route.HOMEOWNER_APP_SAVED_DISPLAY_FULLSCREEN.path, {
        aiDisplayImageId,
      });
    }

    const { params: projectParams } = matchPath(referrer, {
      path: Route.HOMEOWNER_APP_PROJECT.path,
    }) ?? { params: {} };
    const { projectId } = parseParams(
      projectParams,
      object({ projectId: number() }),
    );

    if (projectParams && projectId) {
      return generatePath(Route.HOMEOWNER_APP_PROJECT_DISPLAY_FULLSCREEN.path, {
        aiDisplayImageId,
        projectId,
      });
    }

    const error = new Error(
      [
        `useCloseCustomize: unhandled referrer "${referrer}".`,
        "I don't know where to send the user when exiting the customize flow.",
      ].join(' '),
    );

    if (isDevelopment()) throw error;

    logError(error);

    return Route.HOMEOWNER_APP_HOME.path;
  }, [image, referrer]);

  const { projects } = useGetProjectsQuery().data ?? {};
  const hasNoProjects = !!projects && projects.length === 0;

  const closeState = useMemo<SaveToProjectState | undefined>(() => {
    if (!image) return undefined;

    return hasNoProjects
      ? { tag: 'save-to-project', aiDisplayImageId: image.id }
      : undefined;
  }, [hasNoProjects, image]);

  const close = useCallback(
    async ({ edited }: { edited: boolean }) => {
      setIsClosing(true);

      try {
        await loadRoute(closePathname);
      } finally {
        history.push(closePathname, edited ? closeState : undefined);
        setIsClosing(false);
      }
    },
    [closePathname, closeState, history, loadRoute],
  );

  const preload = useCallback(() => {
    onPreloadClose();

    if (!hasPreloadedClose) preloadRoute(closePathname);
  }, [closePathname, hasPreloadedClose, onPreloadClose, preloadRoute]);

  return {
    preload,
    isClosing,
    cancel: useCallback(() => close({ edited: false }), [close]),
    save: useCallback(() => close({ edited: true }), [close]),
  };
};
